import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import * as React from "react";

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

const isMac =
  typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const modKey = isMac ? "⌘" : "Ctrl";

interface ShortcutItem {
  keys: string[];
  description: string;
}

interface ShortcutCategory {
  title: string;
  shortcuts: ShortcutItem[];
}

const shortcutCategories: ShortcutCategory[] = [
  {
    title: "Tools",
    shortcuts: [
      { keys: ["V"], description: "Select tool" },
      { keys: ["A"], description: "Area tool" },
      { keys: ["Z"], description: "Zone tool" },
      { keys: ["L"], description: "Line tool" },
      { keys: ["S"], description: "Spot tool" },
      { keys: ["T"], description: "Text tool" },
    ],
  },
  {
    title: "Shape Operations",
    shortcuts: [
      { keys: ["Delete"], description: "Delete selected" },
      { keys: [modKey, "D"], description: "Duplicate selected" },
      { keys: [modKey, "Z"], description: "Undo" },
      { keys: [modKey, "A"], description: "Select all" },
      { keys: ["Esc"], description: "Deselect all" },
    ],
  },
  {
    title: "Movement",
    shortcuts: [
      { keys: ["←", "→", "↑", "↓"], description: "Move shape 1px" },
      { keys: ["Shift", "←"], description: "Rotate counter-clockwise" },
      { keys: ["Shift", "→"], description: "Rotate clockwise" },
      { keys: ["Shift", "↑"], description: "Scale up 1%" },
      { keys: ["Shift", "↓"], description: "Scale down 1%" },
    ],
  },
  {
    title: "Reference Aircraft",
    shortcuts: [
      { keys: ["R"], description: "Rotate +15°" },
      { keys: ["Shift", "R"], description: "Rotate -15°" },
    ],
  },
  {
    title: "Layer Order",
    shortcuts: [
      { keys: [modKey, "["], description: "Send backward" },
      { keys: [modKey, "]"], description: "Bring forward" },
      { keys: [modKey, "Shift", "["], description: "Send to back" },
      { keys: [modKey, "Shift", "]"], description: "Bring to front" },
    ],
  },
  {
    title: "General",
    shortcuts: [
      { keys: [modKey, "S"], description: "Save" },
      { keys: [modKey, "2"], description: "Zoom in" },
      { keys: [modKey, "1"], description: "Zoom out" },
    ],
  },
];

const KeyBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 24,
      height: 24,
      px: 0.75,
      borderRadius: 0.5,
      bgcolor: "action.hover",
      border: "1px solid",
      borderColor: "divider",
      fontSize: "0.75rem",
      fontFamily: "inherit",
      fontWeight: 500,
    }}
  >
    {children}
  </Box>
);

export const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  open,
  onClose,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography variant="h6">Keyboard Shortcuts</Typography>
        <IconButton size="small" onClick={onClose} edge="end">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          {shortcutCategories.map((category) => (
            <Box key={category.title}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 600 }}
              >
                {category.title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {category.shortcuts.map((shortcut, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {shortcut.description}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {shortcut.keys.map((key, keyIdx) => (
                        <KeyBadge key={keyIdx}>{key}</KeyBadge>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
